import { mdiCloudPrint, mdiFeatureSearch, mdiMapCheck, mdiTagHeartOutline, mdiDomain, mdiArrowUpBoldCircleOutline, mdiSwapHorizontal } from "@mdi/js";
import type { MainViewMode } from "~/types/entity/cWebCatalog";
import type { Role } from "~/types/entity/user";

// 状態を保持しない純粋関数として表現できるページと権限の関係の記述
// 権限表 順番に権限を増やしたいため const 変数割り当てをしている
// 会員区分別アクセス制御 (2026-07-06 権限再編。Documents/Plan/Trial3minutes/Plan20260706.md §2 権限表)
// const abovePlatinumRoles: Role[] = ["Platinum"];

// フル機能 (持ち出し系=印刷/エクスポート/pixiv を含む) = Gold 相当。FreeRenewal は無料期間のフル体験
export const aboveGoldRoles: Role[] = ["Platinum", "Gold", "FreeRenewal"];

// サークル閲覧系 (サークル一覧/詳細・お気に入り・会場マップ = 権限表の△ページ) を無制限に使える Role。
// Staff (スタッフSilver。表示名「準備会」) は 3分時限閲覧の対象外=無制限 (Plan20260706 §2)。
// Silver/Bronze はここに含めない: 3分時限閲覧 (時限閲覧=12 保持中) のみ利用可で、
// ブロック中のアクセスは課金ページへリダイレクトする (checkPermission / useTrialUplift 参照)。
export const unlimitedViewRoles: Role[] = [...aboveGoldRoles, "Staff"];

/**
 * EntraID / AzureAD B2C ログインを使うなら true
 * C105以前の circle.ms 共通認証クッキーを使うなら false
 */
export const useEntraId2Token = false;

/**
 * メニュー表示レベル
 * null : 非表示
 * 1 : トップメニューとして表示
 * 2 : サブメニューとして表示 (2 以上の数値をどのように表示するかは component に依存する)
 */
type menuLevel = null | "main" | "submenu"

type NavigationSetting = {
  viewMode: MainViewMode
  /**
   * 遷移先のパス
   * メニューとして存在しない (詳細画面など) の場合は null
   */
  route: string | null
  /*
   * 保持していれば利用可能になる Role の配列
   * null の場合は Role を要求しない
   */
  roles: Role[] | null
  icon?: string
  pcMenu: menuLevel
  mobileMenu: menuLevel
  isBooth?: boolean
  // C105までは 企業ブース表示期間前に企業ブース表示させないために使っていた
  // disabled?: boolean
  /**
   * 外部リンクの場合は true
   */
  isExternal?: boolean
}

export const navigationSettingsAll: NavigationSetting[] = [
  {
    viewMode: "TOP_PAGE",
    route: "/",
    roles: null, // TOP は全会員 + 未ログインに開放 (未ログインの描画は index.vue 側で対応)
    icon: mdiArrowUpBoldCircleOutline,
    pcMenu: "main",
    mobileMenu: "submenu",
  },
  {
    viewMode: "CIRCLE_LIST",
    route: "/circle/list",
    roles: unlimitedViewRoles, // △: 無制限は unlimitedViewRoles。Bronze/Silver は3分時限閲覧(checkPermission で trialActive を併用)
    icon: mdiFeatureSearch,
    pcMenu: "main",
    mobileMenu: "main",
    isBooth: false
  },
  {
    viewMode: "BOOTH_LIST",
    route: "/booth/list",
    roles: null, // 企業ページは 全会員に開放
    icon: mdiDomain,
    pcMenu: "main",
    mobileMenu: "submenu",
    isBooth: true, // !webCatalogBoothData.isPublishedBooth()
  },
  {
    viewMode: "FAVORITE",
    route: "/user/favorites",
    roles: unlimitedViewRoles, // △: 無制限は unlimitedViewRoles。Bronze/Silver は3分時限閲覧(checkPermission で trialActive を併用)
    icon: mdiTagHeartOutline,
    pcMenu: "main",
    mobileMenu: "main",
  },
  {
    viewMode: "FAVORITE_BOOTH",
    route: "/user/favoritebooth",
    roles: unlimitedViewRoles, // △: 無制限は unlimitedViewRoles。Bronze/Silver は3分時限閲覧(checkPermission で trialActive を併用)
    icon: mdiTagHeartOutline,
    pcMenu: "main",
    mobileMenu: "main",
  },
  {
    // route(ページ)が存在しないが viewMode 自体は importExport.vue の権限チェック・タイトルキーで使用中
    viewMode: "IMPORT_EXPORT",
    route: null,
    roles: aboveGoldRoles, // 持ち出し系: Silver/Bronze は課金導線
    icon: mdiSwapHorizontal,
    pcMenu: null,
    mobileMenu: null,
  },
  {
    viewMode: "PIXIV_IMPORT",
    route: "/user/pixivImport",
    roles: null, // 外部連携(インポート)は全会員に開放 (2026-07-06 権限再編。Plan20260706 §5.4)
    pcMenu: null,
    mobileMenu: null,
  },
  {
    viewMode: "PIXIV_EXPORT",
    route: "/user/pixivExport",
    roles: aboveGoldRoles, // 外部連携(エクスポート, 持ち出し系): Silver/Bronze は課金導線
    pcMenu: null,
    mobileMenu: null,
  },
  {
    viewMode: "COMIKE_MAP",
    route: "/map",
    roles: unlimitedViewRoles, // △: 無制限は unlimitedViewRoles。Bronze/Silver は3分時限閲覧(checkPermission で trialActive を併用)
    icon: mdiMapCheck,
    pcMenu: "main",
    mobileMenu: "main",
  },
  {
    viewMode: "COMIKE_BOOTH_MAP",
    route: "/mapBooth",
    roles: null, // 企業ブースMAPは全会員(Bronze含む)に開放
    icon: mdiMapCheck,
    pcMenu: null, // 全体メニューには出さない（企業一覧のボタンから遷移）
    mobileMenu: null,
  },
  {
    viewMode: "COMIKE_PRINT",
    route: "/print",
    roles: aboveGoldRoles, // 印刷 (持ち出し系): Silver/Bronze は課金導線
    icon: mdiCloudPrint,
    pcMenu: "main",
    mobileMenu: "submenu",
    isBooth: false
  },
  {
    viewMode: "COMIKE_PRINT_DETAIL", // 実際の印刷画面
    route: null, // print-type-[printType]
    roles: aboveGoldRoles, // 印刷実行 (持ち出し系): Silver/Bronze は課金導線
    pcMenu: null,
    mobileMenu: null,
  },
  {
    viewMode: "COMIKE_PRINT_PAPERINDEX",
    route: "/print/paperindex",
    roles: null,
    pcMenu: null,
    mobileMenu: null,
  },
  {
    viewMode: "LOGIN",
    route: useEntraId2Token ? "/auth/msal" : "/auth/login",
    roles: null,
    pcMenu: null,
    mobileMenu: null,
  },
  {
    viewMode: "LOGOUT",
    route: useEntraId2Token ? "/auth/msal-logout" : "/auth/auth2-logout",
    roles: null,
    pcMenu: null,
    mobileMenu: null,
  },
  {
    viewMode: "CIRCLE_DETAIL",
    route: null, // circle/[id].vue
    roles: unlimitedViewRoles, // △: 無制限は unlimitedViewRoles。Bronze/Silver は3分時限閲覧(checkPermission で trialActive を併用)
    pcMenu: null,
    mobileMenu: null,
  },
  {
    viewMode: "BOOTH_DETAIL",
    route: null, // booth/[id].vue
    roles: null, // 企業ページは 全会員に開放
    pcMenu: null,
    mobileMenu: null,
  },
  {
    viewMode: "PAYMENT_START",
    route: "/pay",
    roles: null,
    pcMenu: null,
    mobileMenu: null,
  },
  {
    viewMode: "PAYMENT_SELECT_PLAN",
    route: "/pay/selectPlan",
    roles: null,
    pcMenu: null,
    mobileMenu: null,
  },
  {
    viewMode: "PAYMENT_PROCESS",
    route: "/pay/process",
    roles: null,
    pcMenu: null,
    mobileMenu: null,
  },
  {
    viewMode: "PAYMENT_CANCEL",
    route: "/pay/cancel",
    roles: null,
    pcMenu: null,
    mobileMenu: null,
  },
  {
    viewMode: "RESTRICTION",
    route: "/restriction",
    roles: null,
    pcMenu: null,
    mobileMenu: null,
  },
  {
    viewMode: "NO_ACTIVE_USER",
    route: "/auth/no-user",
    roles: null,
    pcMenu: null,
    mobileMenu: null,
  },
  {
    viewMode: "HELP",
    route: "/docs/help",
    roles: null,
    pcMenu: null,
    mobileMenu: null,
  },
  {
    viewMode: "COMICOS",
    route: "/comicos",
    roles: null,
    pcMenu: null,
    mobileMenu: null,
  },
  {
    viewMode: "TODAY",
    route: "/Today",
    roles: null,
    pcMenu: null,
    mobileMenu: null,
  },
  {
    viewMode: "COMICOS_MS",
    route: "https://comicos.circle.ms/",
    roles: null,
    pcMenu: null,
    mobileMenu: null,
    isExternal: true,
  },
  {
    viewMode: "LP",
    route: "https://webcatalog-lp.circle.ms/lp",
    roles: null,
    pcMenu: null,
    mobileMenu: null,
    isExternal: true,
  },
  {
    // /home/Inquiry を MS ポータルの問い合わせページへリダイレクトするための定義。
    // 問い合わせはポータルで統一する (旧 HomeController.Inquiry => ポータルURL + "Inquiry" の踏襲)。
    // 遷移先は外部 (MS ポータル) であり、このアプリ内のルートではないため route は持たない。
    // 実際の遷移先 URL は home/Inquiry.vue 側でポータル基底URL + "Inquiry" を組み立てる。
    viewMode: "INQUIRY",
    route: null,
    roles: null,
    pcMenu: null,
    mobileMenu: null,
    isExternal: true,
  },
];

export const isAuthorizedRole = (mode: MainViewMode, roles: Role[]): boolean => {
  const setting = navigationSettingsAll.filter(x => x.viewMode == mode)[0];
  if (setting === undefined) {
    return false;
  }
  if (setting.roles === null) {
    return true;
  }
  return roles.some(x => setting.roles?.includes(x));
};

/**
 * 出力/持ち出し系 (印刷/pixiv エクスポート) モードか。ゲートが aboveGoldRoles のモードを指す。
 * 権限表の × (Gold のみ)。3分時限閲覧では解放しない (Documents/Plan/Trial3minutes/Plan20260706.md §2)。
 */
export const isOutputMode = (mode: MainViewMode): boolean =>
  navigationSettingsAll.filter(x => x.viewMode == mode)[0]?.roles === aboveGoldRoles;

/**
 * 3分時限閲覧の対象 (権限表の△) モードか。ゲートが unlimitedViewRoles のモードを指す
 * (サークル一覧/詳細・お気に入り・会場マップ)。
 * Bronze/Silver は 時限閲覧(12) 保持中 (trialActive) のみ利用でき、
 * ブロック中のアクセスは課金ページ /pay?from= へリダイレクトする
 * (2026-07-06 権限再編。Documents/Plan/Trial3minutes/Plan20260706.md §6)。
 */
export const isTimeboxedMode = (mode: MainViewMode): boolean =>
  navigationSettingsAll.filter(x => x.viewMode == mode)[0]?.roles === unlimitedViewRoles;

export const getNavigationRoute = (mode: MainViewMode): string | null | undefined =>
  navigationSettingsAll.filter(x => x.viewMode == mode).map(x => x.route)[0];

/**
 * 課金ページ (/pay) への遷移 URL に、遷移元ページの path を from として付与する。
 * 3分時限閲覧のブロック中リダイレクトで「なぜリダイレクトされたか」を /pay 側で表示するために使う
 * (2026-07-06 権限再編。Documents/Plan/Trial3minutes/Plan20260706.md §6 / Q-P5)。
 */
export const getPaymentRouteWithFrom = (fromFullPath: string): string =>
  `${getNavigationRoute("PAYMENT_START")}?from=${encodeURIComponent(fromFullPath)}`;

/**
 * 3分時限閲覧のブロック中リダイレクトで /pay に渡される from (遷移元 path) の機能種別。
 * from を付けてリダイレクトするのは△5ページのみ (Plan20260706 §6 C9)。
 * /pay 側で「機能一覧表の該当行ハイライト」と「課金完了後の復帰先検証」に使う (§12.2 / §12.5)。
 */
export type TimeboxedFeature = "circleList" | "circleDetail" | "favorite" | "map";

// from の path → 機能種別。判定順に評価する (circleList を circleDetail より先に置く)。
// route.fullPath は実際にアクセスした URL のため大文字小文字が混在し得る (例 /user/favoriteBooth)。
// 小文字化した path で判定する。pathname は続く文字が末尾/"/"/"?" のいずれかであることを要求し、
// /map が /mapBooth に、/user/favorites が別 path に部分一致するのを防ぐ (§12 △5ページのみ受理)。
const TIMEBOXED_FEATURE_PATHS = [
  { pathname: "/circle/list", feature: "circleList" },
  { pathname: "/user/favorites", feature: "favorite" },
  { pathname: "/user/favoritebooth", feature: "favorite" },
  { pathname: "/map", feature: "map" },
] as const satisfies readonly { pathname: string; feature: TimeboxedFeature }[];

// pathname が base に一致するか (完全一致 or base の直後が "/")。QueryString は呼び出し側で除去済み。
const matchesPathname = (pathname: string, base: string): boolean =>
  pathname === base || pathname.startsWith(`${base}/`);

/**
 * from (遷移元 fullPath) を既知の△5ページに照合し、機能種別を返す。
 * 未知/不正な path は undefined (ホワイトリスト方式。オープンリダイレクト・紛らわしい文字列の表示防止)。
 * @param fromFullPath encodeURIComponent 前の生 path (例 "/circle/12345?day=1")
 */
export const resolveTimeboxedFeature = (fromFullPath: string | undefined): TimeboxedFeature | undefined => {
  if (!fromFullPath) return undefined;
  // 内部 path のみ受け付ける (// で始まる protocol-relative URL 等を除外)
  if (!fromFullPath.startsWith("/") || fromFullPath.startsWith("//")) return undefined;
  // QueryString / hash を落とした pathname で判定する
  const pathname = fromFullPath.split(/[?#]/)[0]?.toLowerCase() ?? "";
  const matched = TIMEBOXED_FEATURE_PATHS.find(x => matchesPathname(pathname, x.pathname));
  if (matched) return matched.feature;
  // サークル詳細は /circle/[id] (動的)。/circle/list を除いた /circle/ 配下を詳細とみなす。
  if (pathname.startsWith("/circle/")) return "circleDetail";
  return undefined;
};

// pathname を「安定するまで」繰り返し percent デコードし、各段階で `..` / `\` を検証する。
// 1回だけのデコードだと二重エンコード (%252e%252e → %2e%2e → ..) で bypass されるため、
// 収束するまでループする (収束しない・不正エンコードは拒否)。
const decodePathnameFully = (pathname: string): string | undefined => {
  let current = pathname;
  for (let i = 0; i < 10; i++) {
    // `..` と バックスラッシュ (ブラウザは / と同等に扱う) を含めば拒否
    if (current.includes("..") || current.includes("\\")) return undefined;
    let next: string;
    try {
      next = decodeURIComponent(current);
    } catch {
      return undefined; // 不正な percent シーケンス (%zz 等)
    }
    if (next === current) return current; // これ以上デコードで変化しない = 収束
    current = next;
  }
  return undefined; // 10回で収束しない = 異常とみなし拒否
};

/**
 * from が既知の△ページを指す安全な内部 path なら復帰先として返す。そうでなければ undefined。
 * 課金完了後に元のページへ戻す導線で使う (Plan20260706 §12.5(1))。
 *
 * resolveTimeboxedFeature はホワイトリスト照合を「小文字化した pathname」で行う一方、
 * 戻り値には生の fromFullPath をそのまま使う。/circle/ prefix 一致を通る path に
 * `..` / `%2e%2e` / `%252e%252e` / `\` を紛れ込ませるとナビゲーション時にパストラバーサル
 * し得るため、pathname を完全デコードして追加検証してから返す。
 * 保存された値をナビゲーションに使う側 (pay/process の gotoReturnPath) でも本関数を再適用すること。
 */
export const sanitizeReturnPath = (fromFullPath: string | undefined): string | undefined => {
  if (!fromFullPath || !resolveTimeboxedFeature(fromFullPath)) return undefined;
  const pathname = fromFullPath.split(/[?#]/)[0] ?? "";
  if (decodePathnameFully(pathname) === undefined) return undefined;
  return fromFullPath;
};

// 以下はコンテキスト依存の少ない共通ユーティリティ
/**
 * n個ずつに配列を分割する
 * @param arr
 * @param n
 */
export function* chunksGenerator<T>(arr: T[], n: number): Generator<T[], void> {
  for (let i = 0; i < arr.length; i += n) {
    yield arr.slice(i, i + n);
  }
}

/**
 * 文字列配列に変換する。単一の値だったとしても配列とする。ただし値がなかった場合は undefined
 * @param v
 * @returns
 */
export const convertToStringArray = (v: string | (string | null)[] | null | undefined): string[] | undefined => {
  if (Array.isArray(v)) {
    return v.map(item => (typeof item === "string" ? item : "")).filter(item => item !== "");
  }
  if (typeof v === "string") {
    return [v];
  }
  return undefined;
};

/**
 * lastUpdateAtの値からクエリ文字列を生成する
 * nullまたはundefinedの場合は空文字列を返す
 * @param lastUpdateAt
 * @returns クエリ文字列 (例: "t=12345" または "")
 */
export const buildLastUpdateAtQueryString = (lastUpdateAt: number | null | undefined): string =>
  lastUpdateAt ? `t=${lastUpdateAt}` : "";

export const getCircleDetailUrl = (wcid: number | null | undefined, lastUpdateAt: number | null | undefined) => {
  return `/circle/${wcid}?${buildLastUpdateAtQueryString(lastUpdateAt)}`;
};